import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Grid3x3,
  AlertCircle,
  Package,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  delete_menu_data,
  server_post_data,
  get_view_category,
  show_hide_menu,
  save_update_category,
  get_all_taxes,
} from "../ServiceConnection/serviceconnection";

export default function MenuCategory() {
  const { toast } = useToast();

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  // Dialog states
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    categoryName: "",
    cuisine: "",
    maxPreparingTime: "",
    taxSlab: "",
    excludeFromDiscounts: false,
  });
  const [errors, setErrors] = useState({});

  // Filter categories
  const filteredCategories = useCallback(() => {
    if (!categories || categories.length === 0) return [];

    const query = searchQuery.toLowerCase();
    return categories.filter((category) => {
      return (
        category.item_category.toLowerCase().includes(query) ||
        category.item_cuisine?.toLowerCase().includes(query)
      );
    });
  }, [categories, searchQuery])();

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      categoryName: "",
      cuisine: "",
      maxPreparingTime: "",
      taxSlab: "",
      excludeFromDiscounts: false,
    });
    setErrors({});
    setEditingCategory(null);
  }, []);

  // Handle form field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.categoryName.trim()) {
      newErrors.categoryName = "Category name is required";
    }

    if (!formData.cuisine.trim()) {
      newErrors.cuisine = "Cuisine is required";
    }

    if (!formData.maxPreparingTime.trim()) {
      newErrors.maxPreparingTime = "Max preparing time is required";
    } else if (
      isNaN(formData.maxPreparingTime) ||
      parseInt(formData.maxPreparingTime) <= 0
    ) {
      newErrors.maxPreparingTime = "Please enter a valid number greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Fetch categories data
  const fetchCategories = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_view_category, fd);

      if (!response.data.error) {
        setCategories(
          Array.isArray(response.data.message) ? response.data.message : [],
        );
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, []);

  // Fetch tax slabs
  const fetchTaxSlabs = useCallback(async () => {
    const fd = new FormData();
    fd.append("flag", 0);

    try {
      const response = await server_post_data(get_all_taxes, fd);
      if (!response.data.error) {
        setTaxSlabs(response.data.message || []);
      }
    } catch (error) {
      console.error("Error fetching tax slabs:", error);
    }
  }, []);

  // Open dialog for create
  const handleCreateClick = useCallback(() => {
    resetForm();
    setIsFormDialogOpen(true);
  }, [resetForm]);

  // Open dialog for edit
  const handleEditClick = useCallback(
    async (category) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("flag", 2);
      fd.append("call_id", category.primary_id);

      try {
        const response = await server_post_data(get_view_category, fd);

        if (!response.data.error && response.data.message.length > 0) {
          const data = response.data.message[0];
          setEditingCategory(category);
          setFormData({
            categoryName: data.item_category || "",
            cuisine: data.item_cuisine || "",
            maxPreparingTime: data.max_time_prepare?.toString() || "",
            taxSlab: data.tax_slab?.toString() || "",
            excludeFromDiscounts:
              data.discount_exclude === 1 || data.discount_exclude === "1",
          });
          setErrors({});
          setIsFormDialogOpen(true);
        }
      } catch (error) {
        console.error("Error fetching category:", error);
        toast({
          title: "Error",
          description: "Network error. Please try again.",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // Handle save/update
  const handleSaveChanges = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("cate_id", editingCategory ? editingCategory.primary_id : 0);
    fd.append("item_category", formData.categoryName.trim());
    fd.append("item_cuisine", formData.cuisine.trim());
    fd.append("max_time_prepare", formData.maxPreparingTime.trim());
    fd.append("tax_slab", formData.taxSlab || 0);
    fd.append("discount_exclude", formData.excludeFromDiscounts ? 1 : 0);
    fd.append("testi_image_new", "");

    if (!editingCategory) {
      fd.append("show_hide", 1);
    }

    try {
      const response = await server_post_data(save_update_category, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save category",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editingCategory
            ? "Category updated successfully"
            : "Category created successfully",
        });
        fetchCategories();
        setIsFormDialogOpen(false);
        resetForm();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    formData,
    editingCategory,
    toast,
    fetchCategories,
    resetForm,
  ]);

  // Handle delete
  const handleDeleteClick = useCallback((category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedCategory) return;

    // Check if category has dishes
    if (selectedCategory.dish_count > 0) {
      toast({
        title: "Cannot Delete",
        description: `This category contains ${selectedCategory.dish_count} dishes. Please remove all dishes before deleting.`,
        variant: "destructive",
      });
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag_for", 1);
    fd.append("id_for_delete", selectedCategory.primary_id);

    try {
      const response = await server_post_data(delete_menu_data, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete category",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Deleted",
          description: `${selectedCategory.item_category} has been deleted successfully`,
        });
        fetchCategories();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
    }
  }, [selectedCategory, toast, fetchCategories]);

  // Handle toggle status
  const handleToggleClick = useCallback((category) => {
    setSelectedCategory(category);
    setStatusChangeMessage(
      category.show_hide
        ? "Are you sure you want to deactivate this category?"
        : "Are you sure you want to activate this category?",
    );
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedCategory) return;

    setShowLoaderAdmin(true);
    const newStatus = !selectedCategory.show_hide;

    const fd = new FormData();
    fd.append("item_id", selectedCategory.primary_id);
    fd.append("current_status", newStatus ? 0 : 1);
    fd.append("flag_for", "category");

    try {
      const response = await server_post_data(show_hide_menu, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update status",
          variant: "destructive",
        });
      } else {
        setCategories((prev) =>
          prev.map((item) =>
            item.primary_id === selectedCategory.primary_id
              ? { ...item, show_hide: newStatus }
              : item,
          ),
        );
        toast({
          title: "Success",
          description: `Category ${
            newStatus ? "activated" : "deactivated"
          } successfully`,
        });
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedCategory(null);
    }
  }, [selectedCategory, toast]);

  // Get selected tax details for form
  const selectedTaxDetails = taxSlabs.find(
    (tax) => tax.primary_id === parseInt(formData.taxSlab),
  );

  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchTaxSlabs();
  }, [fetchCategories, fetchTaxSlabs]);

  return (
    <MainLayout>
      <Header
        title="Menu Category Management"
        subtitle="Manage your menu categories and cuisines"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Grid3x3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {categories.length}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Categories
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {categories.filter((c) => c.show_hide).length}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <XCircle className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {categories.filter((c) => !c.show_hide).length}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {categories.reduce((sum, c) => sum + (c.dish_count || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Total Dishes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={handleCreateClick}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </Button>
        </div>

        {/* Categories Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr.No</TableHead>
                <TableHead className="text-muted-foreground">
                  Category Name
                </TableHead>
                <TableHead className="text-muted-foreground">Cuisine</TableHead>
                <TableHead className="text-muted-foreground">
                  No. of Dishes
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Tax Slab
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Discount
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-12 text-muted-foreground"
                  >
                    Loading Categories...
                  </TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Grid3x3 className="w-8 h-8" />
                      <p>No categories found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((category, index) => {
                  const taxInfo = taxSlabs.find(
                    (t) => t.primary_id === category.tax_slab,
                  );

                  return (
                    <TableRow
                      key={category.primary_id}
                      className="border-border hover:bg-muted/30"
                    >
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                            <Grid3x3 className="h-5 w-5 text-primary-foreground" />
                          </div>
                          <p className="font-medium text-foreground">
                            {category.item_category}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {category.item_cuisine || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {category.dish_count || 0} dishes
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {taxInfo ? (
                          <span>
                            {taxInfo.tax_name} ({taxInfo.tax_percent_amount}
                            {taxInfo.tax_type === "percentage" ? "%" : " ₹"})
                          </span>
                        ) : (
                          "No Tax"
                        )}
                      </TableCell>
                      <TableCell>
                        {category.discount_exclude === 1 ||
                        category.discount_exclude === "1" ? (
                          <Badge
                            variant="secondary"
                            className="bg-destructive/10 text-destructive border-0"
                          >
                            Excluded
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="bg-success/10 text-success border-0"
                          >
                            Eligible
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleClick(category)}
                          className={cn(
                            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                            category.show_hide
                              ? "bg-success"
                              : "bg-muted-foreground/30",
                          )}
                        >
                          <span
                            className={cn(
                              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                              category.show_hide
                                ? "translate-x-6"
                                : "translate-x-1",
                            )}
                          />
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(category)}
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(category)}
                            className="hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Form Dialog */}
        <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? "Edit Category" : "Add New Category"}
              </DialogTitle>
              <DialogDescription>
                {editingCategory
                  ? "Update category details and settings"
                  : "Create a new menu category"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Category Name */}
              <div className="space-y-2">
                <Label htmlFor="categoryName">
                  Category Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="categoryName"
                  placeholder="e.g., Beverages, Starters, Main Course"
                  value={formData.categoryName}
                  onChange={(e) =>
                    handleFieldChange("categoryName", e.target.value)
                  }
                  className={errors.categoryName ? "border-destructive" : ""}
                />
                {errors.categoryName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.categoryName}
                  </p>
                )}
              </div>

              {/* Cuisine */}
              <div className="space-y-2">
                <Label htmlFor="cuisine">
                  Cuisine <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="cuisine"
                  placeholder="e.g., Indian, Italian, Chinese"
                  value={formData.cuisine}
                  onChange={(e) => handleFieldChange("cuisine", e.target.value)}
                  className={errors.cuisine ? "border-destructive" : ""}
                />
                {errors.cuisine && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.cuisine}
                  </p>
                )}
              </div>

              {/* Max Preparing Time */}
              <div className="space-y-2">
                <Label htmlFor="maxPreparingTime"></Label>
                Max Preparing Time (in minutes){" "}
                <span className="text-destructive">*</span>
                <Input
                  id="maxPreparingTime"
                  type="number"
                  placeholder="e.g., 15"
                  value={formData.maxPreparingTime}
                  onChange={(e) =>
                    handleFieldChange("maxPreparingTime", e.target.value)
                  }
                  className={
                    errors.maxPreparingTime ? "border-destructive" : ""
                  }
                />
                {errors.maxPreparingTime && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.maxPreparingTime}
                  </p>
                )}
              </div>

              {/* Tax Slab */}
              <div className="space-y-2">
                <Label htmlFor="taxSlab">Tax Slab (Optional)</Label>
                <Select
                  value={formData.taxSlab}
                  onValueChange={(value) => handleFieldChange("taxSlab", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Tax Slab" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No Tax</SelectItem>
                    {taxSlabs.map((tax) => (
                      <SelectItem
                        key={tax.primary_id}
                        value={tax.primary_id.toString()}
                      >
                        {tax.tax_name} ({tax.tax_percent_amount}
                        {tax.tax_type === "percentage" ? "%" : " ₹"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Tax will be applied to all items in this category
                </p>
              </div>

              {/* Exclude from Discounts */}
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div className="space-y-1">
                  <Label
                    htmlFor="excludeFromDiscounts"
                    className="cursor-pointer"
                  >
                    Exclude from Discounts
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Items in this category will not be eligible for any
                    discounts
                  </p>
                </div>
                <Checkbox
                  id="excludeFromDiscounts"
                  checked={formData.excludeFromDiscounts}
                  onCheckedChange={(checked) =>
                    handleFieldChange("excludeFromDiscounts", checked)
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsFormDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin
                  ? "Saving..."
                  : editingCategory
                    ? "Update Category"
                    : "Create Category"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Category</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "
                <strong>{selectedCategory?.item_category}</strong>"?
                {selectedCategory?.dish_count > 0 && (
                  <span className="block mt-2 text-destructive font-semibold">
                    This category contains {selectedCategory.dish_count} dishes
                    and cannot be deleted.
                  </span>
                )}
                {selectedCategory?.dish_count === 0 && (
                  <span className="block mt-2">
                    This action cannot be undone.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={
                  showLoaderAdmin || (selectedCategory?.dish_count || 0) > 0
                }
                className="bg-destructive hover:bg-destructive/90"
              >
                {showLoaderAdmin ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Toggle Status Confirmation Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Category Status</AlertDialogTitle>
              <AlertDialogDescription>
                {statusChangeMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmToggle}
                disabled={showLoaderAdmin}
                className="gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? "Updating..." : "Confirm"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
